import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styles/styled';

import PopupInfo from 'components/CreatingTournament/PopupInfo';
import StakeOption from 'components/CreatingTournament/StakeOption';
import { usePriceConversion } from 'hooks/usePriceConversion';
import { CHAIN_STAKE_CONFIG, ChainKey } from 'config/chainStakeConfig';
import {
  RowText,
  Text,
  IconInfo,
  Row,
  Col,
  ErrorText,
} from 'components/CreatingTournament/TournamentCreating/styled';

interface StakeSectionProps {
  /** Selected chain (drives presets, ticker label, USD rate) */
  chain: ChainKey;
  value: number;
  onChange: (value: number) => void;
  error?: string;
}

const InlineCustom = styled.div`
  margin-top: 10px;
  position: relative;
  display: flex;
  align-items: center;
  background: #292B2E;
  border: 1px solid #52D381;
  border-radius: 12px;
  height: 50px;
  padding: 0 14px;
  box-shadow: 0 0 0 4px rgba(82, 211, 129, 0.18);
  width: 100%;
`;

const InlineCustomInput = styled.input`
  flex: 1 1 auto;
  background: transparent;
  border: 0;
  outline: none;
  color: #D9DDE4;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  padding: 0;
  min-width: 0;

  &::placeholder {
    color: #7A7E8C;
  }
`;

const TickerBadge = styled.span`
  color: #A7A8B2;
  font-size: 14px;
  font-weight: 500;
  margin-left: 8px;
`;

const UsdHint = styled.span`
  color: #7A7E8C;
  font-size: 10px;
  margin-left: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const FullWidthCol = styled.div`
  width: 100%;
  flex: 1 1 100%;
`;

const RateValue = styled.b`
  color: #52D381;
  font-weight: 700;
`;

const STEP_PRECISION = 4;
const round = (n: number, p = STEP_PRECISION): number =>
  Number.isFinite(n) ? Number(n.toFixed(p)) : 0;

const StakeSection: React.FC<StakeSectionProps> = ({ chain, value, onChange, error }) => {
  const config = CHAIN_STAKE_CONFIG[chain];
  const ticker = config.ticker;
  const presets = config.paidPresets;
  const customMin = config.customMin;
  const customMax = config.customMax;

  const presetSet = useMemo(
    () => new Set([0, ...presets].map((n) => round(n))),
    [presets],
  );

  const isPreset = presetSet.has(round(value));

  const [isCustomMode, setIsCustomMode] = useState<boolean>(!isPreset && value > 0);
  const [customDraft, setCustomDraft] = useState<string>(
    !isPreset && value > 0 ? String(value) : '',
  );
  const [showInfo, setShowInfo] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset draft + custom mode when chain changes upstream
  useEffect(() => {
    setIsCustomMode(false);
    setCustomDraft('');
  }, [chain]);

  // Stay in custom mode when value isn't a preset of the current chain
  useEffect(() => {
    if (!presetSet.has(round(value)) && value > 0) {
      setIsCustomMode(true);
      setCustomDraft((prev) => (prev === '' ? String(value) : prev));
    } else {
      setIsCustomMode(false);
    }
  }, [value, presetSet]);

  const { pricePerToken, isCached } = usePriceConversion({
    chain,
    amount: 1,
    debounceMs: 0,
  });

  const getUsd = (amount: number | null): number | null => {
    if (pricePerToken == null || amount == null) return null;
    return amount * pricePerToken;
  };

  const handlePresetClick = (preset: number) => {
    setIsCustomMode(false);
    setCustomDraft('');
    onChange(preset);
  };

  const enterCustomMode = () => {
    setIsCustomMode(true);
    if (!customDraft && value > 0 && !presetSet.has(round(value))) {
      setCustomDraft(String(value));
    }
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleCustomChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const raw = event.target.value;
    if (!/^\d*([.,]\d*)?$/.test(raw)) return;

    const normalized = raw.replace(',', '.');
    setCustomDraft(normalized);

    if (normalized === '' || normalized === '.') {
      onChange(0);
      return;
    }

    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) {
      onChange(0);
      return;
    }
    onChange(numeric);
  };

  const customAmount = isCustomMode && !presetSet.has(round(value)) && value > 0 ? value : null;
  const customUsd = customAmount != null ? getUsd(customAmount) : null;
  const isCustomValid = customAmount != null && customAmount >= customMin && customAmount <= customMax;

  return (
    <>
      <RowText>
        <Text>
          Stake
          <IconInfo onClick={() => setShowInfo(true)} />
        </Text>
        {showInfo && (
          <PopupInfo
            className="smallPopup"
            text="The amount each player adds to the reward pool to join the competition."
            subText=""
            subTextSecond=""
            handleClose={() => setShowInfo(false)}
          />
        )}
      </RowText>

      <Text className="center">
        1 {ticker} ≈{' '}
        {pricePerToken != null ? (
          <RateValue>{pricePerToken.toFixed(4)} USD</RateValue>
        ) : (
          '-- USD'
        )}
        {isCached && pricePerToken != null && ' (cached)'}
      </Text>

      <Row>
        <Col>
          <StakeOption
            label="Free"
            subtitle="No entry"
            selected={value === 0 && !isCustomMode}
            onClick={() => handlePresetClick(0)}
            isCached={isCached}
          />
        </Col>
        <Col>
          <StakeOption
            label={`${presets[0]} ${ticker}`}
            selected={!isCustomMode && round(value) === round(presets[0])}
            onClick={() => handlePresetClick(presets[0])}
            usdValue={getUsd(presets[0])}
            isCached={isCached}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <StakeOption
            label={`${presets[1]} ${ticker}`}
            selected={!isCustomMode && round(value) === round(presets[1])}
            onClick={() => handlePresetClick(presets[1])}
            usdValue={getUsd(presets[1])}
            isCached={isCached}
          />
        </Col>
        <Col>
          <StakeOption
            label={`${presets[2]} ${ticker}`}
            selected={!isCustomMode && round(value) === round(presets[2])}
            onClick={() => handlePresetClick(presets[2])}
            usdValue={getUsd(presets[2])}
            isCached={isCached}
          />
        </Col>
      </Row>

      <Row>
        <FullWidthCol>
          <StakeOption
            label={
              isCustomMode && customAmount != null
                ? `${customAmount} ${ticker}`
                : 'Custom'
            }
            selected={isCustomMode}
            onClick={enterCustomMode}
            usdValue={customUsd}
            isCached={isCached}
          />
        </FullWidthCol>
      </Row>

      {isCustomMode && (
        <InlineCustom role="group" aria-label="Custom stake amount">
          <InlineCustomInput
            ref={inputRef}
            type="text"
            inputMode="decimal"
            placeholder="Enter amount"
            value={customDraft}
            onChange={handleCustomChange}
            aria-label={`Custom stake in ${ticker}`}
            aria-describedby={error ? 'stake-error' : undefined}
            autoFocus
          />
          <TickerBadge>{ticker}</TickerBadge>
          <UsdHint>
            {isCustomValid && customUsd != null
              ? `≈ ${parseFloat(customUsd.toFixed(4))} USD`
              : `${customMin}–${customMax} ${ticker}`}
          </UsdHint>
        </InlineCustom>
      )}

      {error && (
        <ErrorText id="stake-error" role="alert" aria-live="polite">
          {error}
        </ErrorText>
      )}
    </>
  );
};

export default StakeSection;
